import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Space,
  Tabs,
} from 'antd';
import { observer } from 'mobx-react-lite';

import { useResource } from 'app/common/api';
import { DataStore } from 'app/common/data-storage/features/data-storage';
import { usePageNotify } from 'app/common/notifications/features/page-notifications';
import * as PageNotifications from 'app/common/notifications/features/page-notifications';
import { CanAccess } from 'app/railways/domains/access/features/permissions';
import { useAuthStatusWriter } from 'app/railways/domains/auth/features/auth-status';

import { Loading } from '../../../../ui';
import { TEmployee } from '../core/employee';
import { EmployeeRoleList } from '../features/roles/role-list';
import { getEmployeeRequest, updateEmployeeRequest } from '../requests';

const { TabPane } = Tabs;

const ButtonSubmit = observer(() => {
  return (
    <CanAccess perm={['PERM_EMPLOYEE_UPDATE']}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="primary" htmlType="submit">
          Сохранить
        </Button>
      </div>
    </CanAccess>
  );
});

type UpdatedEmployee = Partial<TEmployee> & { id: number };

type EmployeeDetailsProps = {
  id: number;
};

export const EmployeeDetails = observer(({ id }: EmployeeDetailsProps) => {
  const [form] = Form.useForm();

  const [employeeStore] = useState(
    () =>
      new DataStore<TEmployee>({
        id: 0,
        name: '',
        blocked: false,
        login: '',
        roles: [],
      }),
  );
  const authStatusStore = useAuthStatusWriter();
  const pageNotify = usePageNotify();

  const addEmployeeResource = useResource<TEmployee['id'], TEmployee>({
    request: getEmployeeRequest,
    errorNotify: pageNotify,
    onAuthRequired: () => authStatusStore.denyAccess(),
    onDone: (employee) => {
      employeeStore.setData(employee);
      form.setFieldsValue(employee);
    },
  });

  const updateEmployeeResource = useResource<UpdatedEmployee, TEmployee>({
    request: updateEmployeeRequest,
    errorNotify: pageNotify,
    onAuthRequired: () => authStatusStore.denyAccess(),
    onDone: (employee) => {
      employeeStore.setData(employee);
      form.setFieldsValue(employee);
      pageNotify.addMessage({
        type: 'success',
        message: 'Данные сотрудника обновлены',
      });
    },
  });

  const isLoading =
    addEmployeeResource.isLoading || updateEmployeeResource.isLoading;

  useEffect(() => {
    addEmployeeResource.send(id);
  }, []);

  const handleUpdateEmployee = useCallback(
    (updatedEmployee: UpdatedEmployee) => {
      updateEmployeeResource.send({
        ...employeeStore.data,
        ...updatedEmployee,
      });
    },
    [updateEmployeeResource, employeeStore.data],
  );

  return (
    <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
      <PageNotifications.Render>{(notify) => notify}</PageNotifications.Render>

      <Card size="small" bodyStyle={{ paddingTop: 0 }}>
        <Loading active={isLoading}>
          <Tabs>
            <TabPane forceRender tab="Данные аккаунта" key="1">
              {/* @TODO: Че т не рендерится при первом рендере */}
              <Form
                form={form}
                layout="vertical"
                onFinish={handleUpdateEmployee}
              >
                <Row gutter={16}>
                  <Col span={6}>
                    <Form.Item
                      name="login"
                      label="Логин"
                      rules={[{ required: true }]}
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>

                  <Col span={6}>
                    <Form.Item
                      name="name"
                      label="Имя"
                      rules={[{ required: true }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>

                  <Col span={6}>
                    <Form.Item name="password" label="Пароль">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={6}>
                    <Form.Item name="blocked" valuePropName="checked">
                      <Checkbox>Заблокирован</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
                <Divider />
                <ButtonSubmit />
              </Form>
            </TabPane>

            <TabPane tab="Роли и разрешения" key="2">
              <Form layout="vertical" onFinish={handleUpdateEmployee}>
                <Form.Item
                  name="roles"
                  // valuePropName="selected"
                  rules={[
                    {
                      required: true,
                      message: 'Пожалуйста, выберите необходимые роли',
                    },
                  ]}
                >
                  <EmployeeRoleList selected={employeeStore.data.roles} />
                </Form.Item>

                <Divider />

                <ButtonSubmit />
              </Form>
            </TabPane>
          </Tabs>
        </Loading>
      </Card>
    </Space>
  );
});
